import { type ApolloClient, useApolloClient } from '@apollo/client'
import type { ViewQueryList, ViewQueryRecordNode } from '@salescore/core'
import { isNotNullish } from 'effect/Predicate'

import type { RSheetColumn, RSheetRecordNode } from '../../../domain/column'
import { type UpsertViewRecordsReturnType, useUpsertViewRecordsEntry } from '../../../hooks/useUpsertViewRecordsEntry'
import type { RelationChange } from '../../../recoil/records/mutations/updateRecordNodeRelations'
import { getDefaultAttributes } from '../../../recoil/records/selectors/querySelector'
import type { SalescoreObjectAbility, ViewAbility } from '../../../recoil/view/atoms'
import type { RecordChange } from '../../util/updateViewRecords'
import { searchRelation } from './searchRelation'

interface RowChange {
  rowIndex: number
  innerRowIndex: number
  column: RSheetColumn<RSheetRecordNode>
  value: unknown
  label?: string
  isDelete?: boolean
  isDeleteAllChildren?: boolean
}

export async function generateRecordChangeAndRelationChange(
  rowChange: RowChange,
  client: ApolloClient<object>,
  query: ViewQueryList | undefined,
): Promise<{ recordChange: RecordChange; relationChange?: RelationChange }> {
  const { rowIndex, column, innerRowIndex, value, label, isDelete, isDeleteAllChildren } = rowChange
  const { field } = column
  if (field === undefined) {
    // システム系カラムのはず、かつシステム系カラムは別で出し分けしているためここには到達しないはず
    throw new Error('recordChangeAndRelationChange is undefined')
  }

  const recordChange: RecordChange = {
    rowIndex,
    isNestedColumn: field.nodePath.length > 1,
    nodePath: field.nodePath,
    innerRowIndex,
    field,
    value,
    label,
    isDelete,
    isDeleteAllChildren,
  }
  const relationChange = await searchRelation({ recordChange, client, query })
  return {
    recordChange,
    relationChange,
  }
}

export const useUpsertSheetRowMutation = (
  recordNode: ViewQueryRecordNode | undefined,
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList | undefined,
) => {
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)
  const client = useApolloClient()

  return async (rowChange: RowChange): Promise<UpsertViewRecordsReturnType> => {
    const newRecordChanges =
      rowChange.isDelete !== true && recordNode === undefined
        ? [
            {
              rowIndex: rowChange.rowIndex,
              innerRowIndex: rowChange.innerRowIndex,
              parentSelectionInnerRowIndex: rowChange.innerRowIndex,
              height: 1,
              node: rowChange.column.node,
            },
          ]
        : []

    const { defaultValues } = getDefaultAttributes(rowChange.column.node, query)
    const defaultValueChanges = defaultValues.map(
      ({ field, value }): RecordChange => ({
        rowIndex: rowChange.rowIndex,
        innerRowIndex: rowChange.innerRowIndex,
        field,
        nodePath: field.nodePath,
        isNestedColumn: false,
        value,
        label: undefined,
      }),
    )

    const recordChangeAndRelationChange = await generateRecordChangeAndRelationChange(rowChange, client, query)
    const { recordChange, relationChange } = recordChangeAndRelationChange
    return upsertViewRecordsEntry({
      newRecordChanges,
      recordChanges: newRecordChanges.length === 0 ? [recordChange] : [...defaultValueChanges, recordChange],
      relationChanges: [relationChange].filter(isNotNullish),
    })
  }
}

export const useUpsertSheetRowsMutation = (
  ability: ViewAbility & SalescoreObjectAbility,
  query: ViewQueryList | undefined,
) => {
  const upsertViewRecordsEntry = useUpsertViewRecordsEntry(query, ability)
  const client = useApolloClient()

  return async (rowChanges: RowChange[]): Promise<UpsertViewRecordsReturnType> => {
    const recordChanges: RecordChange[] = []
    const relationChanges: RelationChange[] = []
    const recordChangeAndRelationChangePromises = rowChanges.map(
      async (rowChange) => await generateRecordChangeAndRelationChange(rowChange, client, query),
    )
    const results = await Promise.allSettled(recordChangeAndRelationChangePromises)
    for (const result of results.filter(isNotNullish)) {
      if (result.status === 'rejected') {
        continue
      }
      const { recordChange, relationChange } = result.value
      recordChanges.push(recordChange)
      if (relationChange === undefined) {
        continue
      }
      relationChanges.push(relationChange)
    }
    return upsertViewRecordsEntry({
      recordChanges,
      relationChanges,
    })
  }
}
